import * as store from "../state/store.js";
import type { Bounds, Entity, Layer, RawEntity } from "../state/types.js";
import { nextColor } from "../utils/colors.js";
import {
  computeContainmentTree,
  fitCircleRadius,
  isClosedContour,
  pointsBBox,
} from "../utils/geometry.js";
import type { ParsedDxf } from "../dxf/parser.js";
import { rebuildGlbFromCurrent } from "./glb.js";

// ------- point extraction -------
// Fatten an entity into a polyline the renderer + hit-test can use directly.

const CURVE_SEGMENTS = 36;

export function extractPoints(entity: RawEntity): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  try {
    switch (entity.type) {
      case "LINE":
        pts.push([entity.startX ?? 0, entity.startY ?? 0]);
        pts.push([entity.endX ?? 0, entity.endY ?? 0]);
        break;
      case "LWPOLYLINE":
      case "POLYLINE": {
        const verts = entity.vertices;
        if (!verts || verts.length === 0) break;
        const closed = ((entity.flags ?? 0) & 1) === 1;
        const n = verts.length;
        // Walk each segment. `last` is the number of segments to expand:
        // one extra when the polyline is closed so we also include the
        // wrap-around from vertex n-1 back to 0 (which itself can carry a
        // bulge on the final vertex).
        const last = closed ? n : n - 1;
        pts.push([verts[0]!.x ?? 0, verts[0]!.y ?? 0]);
        for (let i = 0; i < last; i++) {
          const v0 = verts[i]!;
          const v1 = verts[(i + 1) % n]!;
          const x0 = v0.x ?? 0;
          const y0 = v0.y ?? 0;
          const x1 = v1.x ?? 0;
          const y1 = v1.y ?? 0;
          const bulge = v0.b ?? 0;
          if (Math.abs(bulge) < 1e-12) {
            pts.push([x1, y1]);
            continue;
          }
          // Bulged segment -- expand to arc. `bulge = tan(theta/4)` where
          // theta is the included angle (signed: +ve = CCW, -ve = CW).
          const chord = Math.hypot(x1 - x0, y1 - y0);
          if (chord === 0) continue;
          const theta = 4 * Math.atan(bulge);
          const radius = chord / (2 * Math.sin(Math.abs(theta) / 2));
          const mx = (x0 + x1) / 2;
          const my = (y0 + y1) / 2;
          // Perpendicular distance from midpoint to arc centre. Guard against
          // tiny negatives from float error before the sqrt.
          const d = Math.sqrt(Math.max(0, radius * radius - (chord / 2) ** 2));
          const ux = (x1 - x0) / chord;
          const uy = (y1 - y0) / chord;
          const sign = bulge > 0 ? 1 : -1;
          const cx = mx - sign * d * uy;
          const cy = my + sign * d * ux;
          const a0 = Math.atan2(y0 - cy, x0 - cx);
          // Segment count proportional to the arc's fraction of a full
          // circle so a full 360 gets CURVE_SEGMENTS (36) and smaller arcs
          // get correspondingly fewer.
          const segs = Math.max(
            2,
            Math.ceil((Math.abs(theta) / (2 * Math.PI)) * CURVE_SEGMENTS)
          );
          for (let k = 1; k <= segs; k++) {
            const a = a0 + theta * (k / segs);
            pts.push([cx + radius * Math.cos(a), cy + radius * Math.sin(a)]);
          }
        }
        break;
      }
      case "CIRCLE": {
        const cx = entity.startX ?? entity.centerX ?? 0;
        const cy = entity.startY ?? entity.centerY ?? 0;
        const r = entity.radius ?? 10;
        for (let i = 0; i <= CURVE_SEGMENTS; i++) {
          const a = (i / CURVE_SEGMENTS) * Math.PI * 2;
          pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
        }
        break;
      }
      case "ARC": {
        const cx = entity.startX ?? entity.centerX ?? 0;
        const cy = entity.startY ?? entity.centerY ?? 0;
        const r = entity.radius ?? 10;
        // DXF ARC always sweeps COUNTER-CLOCKWISE from startAngle to
        // endAngle. When the stored endAngle is numerically less than
        // startAngle, it means the sweep wraps past 360 -- add a full turn
        // so linear interpolation walks the intended CCW arc instead of
        // going the long way clockwise. Without this a 90 degree fillet
        // from 270 to 0 renders as a 270 degree arc looping the wrong way.
        const startDeg = entity.startAngle ?? 0;
        let endDeg = entity.endAngle ?? 0;
        if (endDeg < startDeg) endDeg += 360;
        const startA = (startDeg * Math.PI) / 180;
        const endA = (endDeg * Math.PI) / 180;
        for (let i = 0; i <= CURVE_SEGMENTS; i++) {
          const a = startA + (endA - startA) * (i / CURVE_SEGMENTS);
          pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
        }
        break;
      }
    }
  } catch {
    // Ignore malformed entities — they just don't render.
  }
  return pts;
}

// ------- full DXF ingestion -------
// Replaces the entire model with what's in the parsed DXF, normalising the
// origin so the bottom-left of the drawing sits at (0, 0).

export function processDxf(parsed: ParsedDxf): void {
  const raw = parsed.entities ?? [];
  if (raw.length === 0) {
    resetModel();
    return;
  }

  const layers: Record<string, Layer> = {};
  const layerOrder: string[] = [];
  const entities: Entity[] = [];
  let used: string[] = [];
  let nextId = 0;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const r of raw) {
    const layerName = r.layer || "0";
    const id = nextId++;
    const points = extractPoints(r);
    if (points.length > 0) {
      for (const [x, y] of points) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }

    entities.push({ id, type: r.type, layer: layerName, data: r, points });

    if (!layers[layerName]) {
      const { color, used: nextUsed } = nextColor(used);
      used = nextUsed;
      layers[layerName] = {
        name: layerName,
        color,
        visible: true,
        locked: false,
        entityIds: [],
      };
      layerOrder.push(layerName);
    }
    layers[layerName]!.entityIds.push(id);
  }

  // Normalise so the bottom-left of the drawing sits at (0, 0). The renderer
  // then just centers on that box.
  const offsetX = Number.isFinite(minX) ? minX : 0;
  const offsetY = Number.isFinite(minY) ? minY : 0;

  const shiftedEntities = entities.map((e) => shiftEntity(e, offsetX, offsetY));

  const bounds: Bounds = {
    minX: 0,
    minY: 0,
    maxX: Number.isFinite(maxX) ? maxX - offsetX : 0,
    maxY: Number.isFinite(maxY) ? maxY - offsetY : 0,
  };

  // Rebuild layer entityIds so they point at the shifted entities.
  for (const layer of Object.values(layers)) layer.entityIds = [];
  for (const e of shiftedEntities) {
    layers[e.layer]?.entityIds.push(e.id);
  }

  // "0" is the DXF default layer. When it appears among the file's layers,
  // pin it to the top of the order regardless of when we first encountered
  // an entity on it — matches the DXF spec convention and keeps the panel
  // consistent with post-delete/merge state.
  const normalisedOrder = layerOrder.includes("0")
    ? ["0", ...layerOrder.filter((n) => n !== "0")]
    : layerOrder;

  store.batch(() => {
    store.write.entities(shiftedEntities);
    store.write.layers(layers);
    store.write.layerOrder(normalisedOrder);
    store.write.selectedEntityIds(new Set());
    store.write.selectedLayerNames(new Set());
    store.write.selectedLayer(normalisedOrder[0] ?? null);
    store.write.nextEntityId(nextId);
    store.write.bounds(bounds);
    store.write.isLoaded(true);
    store.write.usedColors(used);
    store.write.filterText("");
  });
}

function shiftEntity(e: Entity, dx: number, dy: number): Entity {
  const data: RawEntity = { ...e.data };
  if (data.startX !== undefined) data.startX -= dx;
  if (data.startY !== undefined) data.startY -= dy;
  if (data.endX !== undefined) data.endX -= dx;
  if (data.endY !== undefined) data.endY -= dy;
  if (data.centerX !== undefined) data.centerX -= dx;
  if (data.centerY !== undefined) data.centerY -= dy;
  if (data.vertices) {
    data.vertices = data.vertices.map((v) => ({ x: v.x - dx, y: v.y - dy }));
  }
  const points = e.points.map(([x, y]) => [x - dx, y - dy] as [number, number]);
  return { ...e, data, points };
}

export function resetModel(): void {
  store.batch(() => {
    store.write.entities([]);
    store.write.layers({});
    store.write.layerOrder([]);
    store.write.selectedEntityIds(new Set());
    store.write.selectedLayerNames(new Set());
    store.write.selectedLayer(null);
    store.write.nextEntityId(0);
    store.write.bounds(null);
    store.write.isLoaded(false);
    store.write.usedColors([]);
    store.write.originalDxf(null);
    store.write.customVars({});
    store.write.loadedUnitCode(null);
    store.write.filterText("");
    store.write.zoom(50);
    store.write.referenceZoom(50);
    store.write.pan({ x: 0, y: 0 });
  });
}

// ------- selection ops -------

export function selectAll(): void {
  const ids = new Set<number>();
  for (const e of store.entities()) {
    const layer = store.layers()[e.layer];
    if (layer?.visible && !layer.locked) ids.add(e.id);
  }
  store.write.selectedEntityIds(ids);
}

export function deselectAll(): void {
  store.write.selectedEntityIds(new Set());
}

// Flip the selection: everything currently selected becomes deselected, and
// every other selectable entity gets selected. Invisible/locked layers are
// skipped in the same way selectAll skips them, so a locked layer's entities
// never sneak into the selection via the flip.
export function invertSelection(): void {
  const current = store.selectedEntityIds();
  const next = new Set<number>();
  for (const e of store.entities()) {
    if (current.has(e.id)) continue;
    const layer = store.layers()[e.layer];
    if (layer?.visible && !layer.locked) next.add(e.id);
  }
  store.write.selectedEntityIds(next);
}

// Flip the selection WITHIN one layer. Currently-selected entities in this
// layer drop out; unselected entities in this layer come in (locked layers
// skipped). Other layers' selection state is left untouched — parallel to
// deselectAllInLayer, which is also layer-scoped.
export function invertSelectionInLayer(name: string): void {
  const layer = store.layers()[name];
  if (!layer || layer.locked) return;
  const current = store.selectedEntityIds();
  const next = new Set(current);
  for (const id of layer.entityIds) {
    if (next.has(id)) next.delete(id);
    else next.add(id);
  }
  store.write.selectedEntityIds(next);
}

// Replace the current selection with every entity in one layer. Locked layers
// are skipped -- the lock invariant (locked layers can't be selected) is
// enforced globally, so we honour it here too.
export function selectAllInLayer(name: string): void {
  const layer = store.layers()[name];
  if (!layer || layer.locked) return;
  store.write.selectedEntityIds(new Set(layer.entityIds));
}

// Remove every entity in one layer from the current selection, leaving
// selections on other layers untouched.
export function deselectAllInLayer(name: string): void {
  const layer = store.layers()[name];
  if (!layer) return;
  const next = new Set(store.selectedEntityIds());
  let changed = false;
  for (const id of layer.entityIds) {
    if (next.delete(id)) changed = true;
  }
  if (changed) store.write.selectedEntityIds(next);
}

export function replaceSelection(ids: Iterable<number>): void {
  store.write.selectedEntityIds(new Set(ids));
}

export function addToSelection(id: number): void {
  const next = new Set(store.selectedEntityIds());
  next.add(id);
  store.write.selectedEntityIds(next);
}

export function toggleInSelection(id: number): void {
  const next = new Set(store.selectedEntityIds());
  if (next.has(id)) next.delete(id);
  else next.add(id);
  store.write.selectedEntityIds(next);
}

export function unionIntoSelection(ids: Iterable<number>): void {
  const next = new Set(store.selectedEntityIds());
  for (const id of ids) next.add(id);
  store.write.selectedEntityIds(next);
}

export function toggleIntoSelection(ids: Iterable<number>): void {
  const next = new Set(store.selectedEntityIds());
  for (const id of ids) {
    if (next.has(id)) next.delete(id);
    else next.add(id);
  }
  store.write.selectedEntityIds(next);
}

// ------- shape-based selection helpers -------
// All three ops accept an optional layer name to scope the search. Used both
// by the canvas context menu (unscoped) and the layer-panel context menu
// (scoped to the right-clicked layer).

function entitiesForScope(layerName?: string): Entity[] {
  const all = store.entities();
  return layerName ? all.filter((e) => e.layer === layerName) : all;
}

export function selectAllRoundHoles(layerName?: string): void {
  // "Round hole" = a circular contour that lives INSIDE some other contour.
  // A perfect circle that IS the outer boundary of the part isn't a hole,
  // it's the part. Use the same cross-layer containment tree that
  // selectAllInnerCutouts uses so a circle enclosed by a rectangle on
  // another layer still qualifies as a hole.
  const closedAll = store.entities().filter(isClosedContour);
  const tree = computeContainmentTree(closedAll);
  const scopeSet = layerName
    ? new Set(entitiesForScope(layerName).map((e) => e.id))
    : null;
  const ids: number[] = [];
  for (const e of closedAll) {
    if (tree.get(e.id) == null) continue;   // top-level = outer, not a hole
    if (scopeSet && !scopeSet.has(e.id)) continue;
    if (fitCircleRadius(e) != null) ids.push(e.id);
  }
  replaceSelection(ids);
}

export function selectAllInnerCutouts(layerName?: string): void {
  // The containment tree needs ALL closed contours (across layers) to spot
  // outer/inner nesting correctly -- a hole in one layer might be enclosed
  // by an outer contour in a different layer. Compute the tree unscoped,
  // then filter the *result* to the requested layer.
  const closedAll = store.entities().filter(isClosedContour);
  const tree = computeContainmentTree(closedAll);
  const ids: number[] = [];
  const scopeSet = layerName ? new Set(entitiesForScope(layerName).map((e) => e.id)) : null;
  for (const e of closedAll) {
    if (tree.get(e.id) == null) continue;   // top-level = outer, not a cutout
    if (scopeSet && !scopeSet.has(e.id)) continue;
    ids.push(e.id);
  }
  replaceSelection(ids);
}

// Match tolerance used by "Select All Similar Cutouts". 1% is tight enough to
// distinguish holes of different nominal sizes (e.g. M4 vs M5 clearance) while
// tolerating minor float drift or micron-level authoring variance.
const SIMILAR_TOL_FRAC = 0.01;

export function selectAllSimilarTo(anchorId: number): void {
  const all = store.entities();
  const anchor = all.find((e) => e.id === anchorId);
  if (!anchor || !isClosedContour(anchor)) return;

  const anchorRadius = fitCircleRadius(anchor);
  if (anchorRadius != null) {
    // Round: match on radius within tolerance.
    const ids: number[] = [];
    for (const e of all) {
      const r = fitCircleRadius(e);
      if (r == null) continue;
      if (Math.abs(r - anchorRadius) / anchorRadius <= SIMILAR_TOL_FRAC) {
        ids.push(e.id);
      }
    }
    replaceSelection(ids);
    return;
  }

  // Irregular: match on bbox width AND height (rotation-agnostic -- a shape
  // rotated 90 degrees swaps its bbox dims but is still "the same shape").
  const b0 = pointsBBox(anchor.points);
  const w0 = b0.maxX - b0.minX;
  const h0 = b0.maxY - b0.minY;
  if (w0 <= 0 || h0 <= 0) return;
  const ids: number[] = [];
  for (const e of all) {
    if (!isClosedContour(e)) continue;
    if (fitCircleRadius(e) != null) continue;   // skip round holes here
    const b = pointsBBox(e.points);
    const w = b.maxX - b.minX;
    const h = b.maxY - b.minY;
    const matchDirect =
      Math.abs(w - w0) / w0 <= SIMILAR_TOL_FRAC &&
      Math.abs(h - h0) / h0 <= SIMILAR_TOL_FRAC;
    const matchRotated =
      Math.abs(w - h0) / h0 <= SIMILAR_TOL_FRAC &&
      Math.abs(h - w0) / w0 <= SIMILAR_TOL_FRAC;
    if (matchDirect || matchRotated) ids.push(e.id);
  }
  replaceSelection(ids);
}

// ------- move to layer -------

export function moveSelectedToLayer(targetName: string): number {
  const target = store.layers()[targetName];
  if (!target) return 0;
  const ids = store.selectedEntityIds();
  if (ids.size === 0) return 0;

  const layers: Record<string, Layer> = {};
  for (const [name, layer] of Object.entries(store.layers())) {
    layers[name] = {
      ...layer,
      entityIds: layer.entityIds.filter((id) => !ids.has(id)),
    };
  }
  const targetLayer = layers[targetName]!;
  layers[targetName] = {
    ...targetLayer,
    entityIds: [...targetLayer.entityIds, ...Array.from(ids)],
  };

  const entities = store.entities().map((e) =>
    ids.has(e.id) ? { ...e, layer: targetName } : e
  );

  store.batch(() => {
    store.write.layers(layers);
    store.write.entities(entities);
    store.write.selectedEntityIds(new Set());
  });

  // Moving entities to a different layer changes per-layer XOR fill in
  // `dxf_2_gltf.rings_to_faces` — see rebuildGlbFromCurrent's comment. If
  // the target layer already had entities, the moved rings can cancel or
  // reveal nested holes that used to sit on different layers.
  rebuildGlbFromCurrent();

  return ids.size;
}
